/**
 * ExerciseSlotEditor — one exercise card inside a block.
 *
 * In a Standalone block there is exactly one slot. In a Superset/Circuit
 * block there are 2+ slots, executed in interleaved rounds at runtime.
 * Each slot owns its own list of difficulty levels (Level 1 / 2 / 3)
 * and each level owns its own sets_data.
 *
 * Drag handle on the slot header is wired by the parent BlockEditor —
 * we expose a `draggable` wrapper around the whole card and the parent
 * controls cross-slot + cross-block reordering through HTML5 dataTransfer.
 */
import { useState } from "react";
import { GripVertical, Plus, Trash2, AlertCircle, Layers } from "lucide-react";
import LevelEditor from "./LevelEditor";
import { emptyLevel } from "@/lib/workoutTaxonomy";

export default function ExerciseSlotEditor({
    category,
    exercise,
    slotIndex,            // 0-based index inside block.exercises[]
    blockIndex,           // 0-based index inside workout.blocks[]
    onChange,
    onRemove,
    canRemove,
    onMoveUp,
    onMoveDown,
    validationError = null,
    forceActiveLevel,
}) {
    const [activeLevel, setActiveLevel] = useState(0);

    const update = (patch) => onChange({ ...exercise, ...patch });

    const updateLevel = (li, lv) =>
        update({ levels: exercise.levels.map((x, idx) => (idx === li ? lv : x)) });

    const addLevel = () => {
        const next = [...exercise.levels, emptyLevel(exercise.levels.length + 1)];
        update({ levels: next });
        setActiveLevel(next.length - 1);
    };

    const removeLevel = (li) => {
        if (exercise.levels.length <= 1) return;
        const next = exercise.levels.filter((_, idx) => idx !== li);
        update({ levels: next.map((l, idx) => ({ ...l, level: idx + 1 })) });
        setActiveLevel(Math.max(0, Math.min(activeLevel, next.length - 1)));
    };

    // Parent can force-switch the active level (e.g., to scroll to a
    // validation error). The `forceActiveLevel` value is bumped on change
    // so the same level re-triggers the effect.
    if (forceActiveLevel && typeof forceActiveLevel.level === "number" && forceActiveLevel.level !== activeLevel) {
        // Defer state update to next tick so render doesn't flag setState-during-render
        setTimeout(() => setActiveLevel(forceActiveLevel.level), 0);
    }

    const safeIdx = Math.min(activeLevel, exercise.levels.length - 1);
    const activeLevelData = exercise.levels[safeIdx];

    const testIdBase = `block-${blockIndex}-slot-${slotIndex}`;

    return (
        <div
            data-testid={testIdBase}
            data-slot-index={slotIndex}
            className="border border-zinc-900 bg-zinc-950"
        >
            {/* Header */}
            <div className="px-4 py-2.5 border-b border-zinc-900 bg-zinc-900/40 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <span title="Drag exercise" className="cursor-grab text-zinc-500 hover:text-brand-red">
                        <GripVertical className="w-4 h-4" />
                    </span>
                    <span className="inline-flex items-center justify-center w-7 h-7 bg-zinc-800 text-white">
                        <Layers className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-semibold">
                        Slot {slotIndex + 1}
                    </span>
                    {exercise.levels[0]?.exercise_name && (
                        <span className="text-xs text-white truncate ml-2">
                            {exercise.levels[0].exercise_name}
                        </span>
                    )}
                    {validationError && (
                        <AlertCircle className="w-3.5 h-3.5 text-brand-red ml-1" />
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {onMoveUp && (
                        <button
                            type="button"
                            data-testid={`${testIdBase}-up`}
                            onClick={onMoveUp}
                            title="Move exercise up"
                            className="text-zinc-400 hover:text-brand-red p-1 text-xs"
                        >▲</button>
                    )}
                    {onMoveDown && (
                        <button
                            type="button"
                            data-testid={`${testIdBase}-down`}
                            onClick={onMoveDown}
                            title="Move exercise down"
                            className="text-zinc-400 hover:text-brand-red p-1 text-xs"
                        >▼</button>
                    )}
                    {canRemove && (
                        <button
                            type="button"
                            data-testid={`${testIdBase}-remove`}
                            onClick={onRemove}
                            title="Remove exercise from block"
                            className="text-zinc-400 hover:text-brand-red p-1"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>

            {/* Level tabs (only when multiple levels) */}
            {exercise.levels.length > 1 && (
                <div className="px-4 py-2 border-b border-zinc-900 flex items-center gap-2 overflow-x-auto">
                    {exercise.levels.map((_, li) => {
                        const tabHasError = validationError?.levelIdx === li;
                        return (
                            <button
                                key={li}
                                type="button"
                                data-testid={`${testIdBase}-level-tab-${li}`}
                                onClick={() => setActiveLevel(li)}
                                className={`text-[10px] tracking-[0.2em] uppercase border px-3 py-1.5 flex-shrink-0 inline-flex items-center gap-1.5 ${activeLevel === li ? "border-brand-red text-brand-red" : tabHasError ? "border-brand-red/70 text-red-300" : "border-zinc-800 text-zinc-200 hover:text-white"}`}
                            >
                                {tabHasError && <AlertCircle className="w-3 h-3" />}
                                Level {li + 1}
                            </button>
                        );
                    })}
                    <button
                        type="button"
                        data-testid={`${testIdBase}-add-level`}
                        onClick={addLevel}
                        className="text-[10px] tracking-[0.2em] uppercase border border-dashed border-zinc-700 text-zinc-200 hover:text-brand-red hover:border-brand-red px-3 py-1.5 inline-flex items-center gap-1.5 flex-shrink-0"
                    >
                        <Plus className="w-3 h-3" /> Add level
                    </button>
                </div>
            )}
            {exercise.levels.length === 1 && (
                <div className="px-4 py-1.5 border-b border-zinc-900 flex items-center justify-end">
                    <button
                        type="button"
                        data-testid={`${testIdBase}-add-level`}
                        onClick={addLevel}
                        className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-brand-red inline-flex items-center gap-1"
                    >
                        <Plus className="w-3 h-3" /> Add difficulty level
                    </button>
                </div>
            )}

            <div className="px-4 py-4">
                {activeLevelData && (
                    <LevelEditor
                        category={category}
                        level={activeLevelData}
                        onChange={(next) => updateLevel(safeIdx, next)}
                        onRemove={() => removeLevel(safeIdx)}
                        canRemove={exercise.levels.length > 1}
                        testIdBase={`${testIdBase}-level-${safeIdx}`}
                        validationError={validationError?.levelIdx === safeIdx ? validationError : null}
                    />
                )}
            </div>
        </div>
    );
}
